import { Box, IconButton, Icons, styled } from '@arpeggi/ui'
import { observer } from 'mobx-react-lite'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import useProfileAPI from '../../../__legacy__/api/ProfileAPI'
import { segmentIdentifyUser } from '../../lib/segment'
import { useUpdateUser } from '../../queries/users'
import AuthStore from '../../stores/AuthStore'
import { formatBytes, toBytes } from '../../utils/bytes'
import { SelectProfilePicture } from '../AuthFlow/SelectProfilePicture'
import Button from '../core/Button/Button'
import { FieldErrorLabel } from '../core/FieldErrorLabel'
import { FieldLabel } from '../core/FieldLabel'
import { Flex } from '../core/Flex'
import Spacer from '../core/Spacer/Spacer'
import Text from '../core/Text/Text'
import { TextArea } from '../core/TextArea'
import { TextInput } from '../core/TextInput'
import { Spinner } from '../platform/explore/common/utils'
import { EditProfileFlowPosition } from './EditProfileFlowModal'

const StyledTextInput = styled(TextInput, {
  marginBottom: '0 !important',
  width: '100%'
})

const StyledIconButton = styled(IconButton, {
  position: 'absolute',
  transform: 'translate(0,-50%)',
  top: '50%',
  right: '5px'
})

export type CreateAccountForm = {
  displayName: string
  bio: string
  twitterHandle: string
  openSeaHandle: string
  soundCloudHandle: string
}

const maxProfileSizeInBytes = toBytes(1, 'MB')

export const EditProfile = observer(
  ({
    setFlowPosition
  }: {
    setFlowPosition: React.Dispatch<React.SetStateAction<EditProfileFlowPosition>>
  }) => {
    const { setUser, user } = AuthStore
    const updateUser = useUpdateUser(user.wallet_address)
    const { getIsValidDisplayName } = useProfileAPI()
    const [profilePicture, setProfilePicture] = useState<File | null>(null)
    const [profilePictureError, setProfilePictureError] = useState<string | null>(null)
    const { enqueueSnackbar } = useSnackbar()
    const {
      control,
      handleSubmit,
      formState: { errors }
    } = useForm<CreateAccountForm>({
      mode: 'onBlur',
      defaultValues: {
        displayName: user.display_name ?? '',
        bio: user.bio ?? '',
        twitterHandle: user.twitter_handle ?? '',
        openSeaHandle: user.opensea_handle ?? '',
        soundCloudHandle: user.soundcloud_handle ?? ''
      }
    })

    const onSelectProfilePicture = (profilePictureFile: File) => {
      if (profilePictureFile.size > maxProfileSizeInBytes) {
        setProfilePictureError(`File size cannot exceed ${formatBytes(maxProfileSizeInBytes)}`)
      } else {
        setProfilePictureError(null)
      }

      setProfilePicture(profilePictureFile)
    }

    const onClickVerifyOnTwitter = () => {
      setFlowPosition('verifyOnTwitter')
    }

    const onClickSubmit: SubmitHandler<CreateAccountForm> = async (data) => {
      if (profilePicture && profilePicture.size > maxProfileSizeInBytes) {
        return
      }

      updateUser.mutate(
        { ...data, profilePicture: profilePicture ?? undefined },
        {
          onSuccess: (data) => {
            enqueueSnackbar('Profile updated!', {
              variant: 'modalSuccess'
            })
            setUser(data)
            // TODO: do we want to identify user here?
            segmentIdentifyUser(data)
          },
          onError: () => {
            enqueueSnackbar('Failed to update profile updated, please try again', {
              variant: 'modalError'
            })
          }
        }
      )
    }

    return (
      <Flex direction={'column'} css={{ justifyContent: 'center', alignItems: 'center' }}>
        <Text css={{ textAlign: 'center' }} size={'32'} type={'primary'}>
          Edit profile
        </Text>

        <Spacer type={'vertical_4'} />

        <SelectProfilePicture onChange={onSelectProfilePicture} profilePicture={profilePicture} />
        <Flex css={{ justifyContent: 'center', marginTop: '8px' }}>
          <FieldErrorLabel>{profilePictureError}</FieldErrorLabel>
        </Flex>

        <Spacer type={'vertical_2'} />

        <form onSubmit={handleSubmit(onClickSubmit)}>
          <Flex direction={'column'} width={'full'}>
            <FieldLabel htmlFor="displayName">Display name</FieldLabel>
            <Controller
              name="displayName"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Required'
                },
                minLength: {
                  value: 4,
                  message: 'At least 4 characters'
                },
                maxLength: {
                  value: 30,
                  message: 'At most 30 characters'
                },
                validate: {
                  uniqueName: async (newDisplayName) =>
                    newDisplayName === user?.display_name ||
                    (await getIsValidDisplayName(newDisplayName)) ||
                    'This display name is already taken'
                }
              }}
              render={({ field }) => (
                <TextInput id="displayName" placeholder="Your display name" {...field} />
              )}
            />
            <FieldErrorLabel>{errors.displayName?.message}</FieldErrorLabel>
          </Flex>

          <Flex direction={'column'} width={'full'}>
            <FieldLabel htmlFor="twitterHandle">Twitter Handle</FieldLabel>
            {user.twitter_handle === null ? (
              <Button
                type="dark"
                big={true}
                rounded={true}
                css={{
                  justifyContent: 'flex-start',
                  width: 'fit-content',
                  backgroundColor: '#1D9BF0'
                }}
                onClick={onClickVerifyOnTwitter}
              >
                <Text size={'14'} type={'primary_inverted'}>
                  Verify on Twitter
                </Text>
              </Button>
            ) : (
              <>
                <Controller
                  name="twitterHandle"
                  control={control}
                  render={({ field }) => (
                    <Box css={{ position: 'relative ' }}>
                      <StyledTextInput
                        disabled={true}
                        id="twitterHandle"
                        placeholder="Your twitter handle"
                        {...field}
                      />
                      <StyledIconButton onClick={onClickVerifyOnTwitter} variant="ghost">
                        <Icons.Edit />
                      </StyledIconButton>
                    </Box>
                  )}
                />
                <FieldErrorLabel css={{ marginTop: '4px' }}>
                  {errors.twitterHandle?.message}
                </FieldErrorLabel>
              </>
            )}
          </Flex>
          <Flex css={{ justifyContent: 'space-between' }}>
            <Flex
              direction={'column'}
              width={'full'}
              css={{
                marginRight: '16px'
              }}
            >
              <FieldLabel htmlFor="openSeaHandle">Opensea handle</FieldLabel>
              <Controller
                name="openSeaHandle"
                control={control}
                rules={{
                  maxLength: {
                    value: 50,
                    message: 'Max 50 characters'
                  }
                }}
                render={({ field }) => (
                  <TextInput id="openSeaHandle" placeholder="Your opensea handle" {...field} />
                )}
              />
              <FieldErrorLabel>{errors.openSeaHandle?.message}</FieldErrorLabel>
            </Flex>

            <Flex direction={'column'} width={'full'}>
              <FieldLabel htmlFor="walletAddress">Soundcloud handle</FieldLabel>
              <Controller
                name="soundCloudHandle"
                control={control}
                rules={{
                  maxLength: {
                    value: 50,
                    message: 'Max 50 characters'
                  }
                }}
                render={({ field }) => (
                  <TextInput
                    id="soundCloudHandle"
                    placeholder="Your soundcloud handle"
                    {...field}
                  />
                )}
              />
              <FieldErrorLabel>{errors.soundCloudHandle?.message}</FieldErrorLabel>
            </Flex>
          </Flex>

          <Flex direction={'column'} width={'full'}>
            <FieldLabel htmlFor="handle">Bio</FieldLabel>
            <Controller
              name="bio"
              control={control}
              rules={{
                maxLength: {
                  value: 160,
                  message: 'Max 160 characters'
                }
              }}
              render={({ field }) => <TextArea id="handle" placeholder="Your Bio..." {...field} />}
            />
            <FieldErrorLabel>{errors.bio?.message}</FieldErrorLabel>
          </Flex>
        </form>

        <Spacer type={'vertical_5'} />

        <Flex direction={'column'} width="full">
          <Button
            type={'dark'}
            big={true}
            rounded={true}
            disabled={updateUser.isLoading}
            onClick={handleSubmit(onClickSubmit)}
          >
            {updateUser.isLoading ? (
              <Spinner />
            ) : (
              <Text type={'primary_inverted'} size={'14'}>
                Update profile
              </Text>
            )}
          </Button>
        </Flex>
      </Flex>
    )
  }
)
